import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Button } from '@components/Button';
import { Loader } from '@components/Loader';
import { Spacer } from '@components/Spacer';

import { verifyEmail, resendEmailVerification, logout } from '@auth_module/services';
import { QUERY_KEYS } from '@constants/queryKeys';
import { SuccessIcon } from '@icons/SuccessIcon';
import { ErrorIcon } from '@icons/ErrorIcon';

const PageContainer = tw.div`h-screen w-screen flex flex-col items-center justify-center bg-cover bg-center bg-fixed bg-page-background bg-gradient-to-t from-[#ffffff] to-[#F6F7FF] px-6 md:px-0`;
const Card = tw.div`w-full max-w-[470px] bg-neutral-background rounded-[12px] relative border border-solid border-gray-1 pt-[24px] overflow-hidden`;
const CardFooter = tw.div`w-full bg-[#EAECF1] text-center text-base text-text-and-icon-muted leading-[150%] h-[48px] flex items-center justify-center`;
const Title = tw.h3`text-[#051129] text-[32px] leading-[140%] font-bold`;
const Subtitle = tw.p`text-[#051129] text-[18px] leading-[150%] font-semibold mb-[32px]`;
const LogoutCTA = tw.button`block text-muted underline cursor-pointer m-auto mt-4`;

const CardButton = tw(
  Button.Primary
)`w-[calc(100% - 8px)]! ml-[4px] mb-[4px] h-[48px]! bg-[#3145C6] py-[12px] rounded-[8px]`;

type VerifyQueryParamKeys = 'signature' | 'expires';

const REDIRECT_TIMEOUT = 3; // 3 sec
const BASE_PAGE_PATH = '/email/verify';
const URL_QUERY_PARAMS = {
  SIGNATURE: 'signature' as VerifyQueryParamKeys,
  EXPIRES: 'expires' as VerifyQueryParamKeys,
};

export const VerifyEmailPage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [error, setError] = useState<Error | { message: string } | null>(null);
  const [redirectCounter, setRedirectCounter] = useState<number>(REDIRECT_TIMEOUT);

  const pathname: string = location.pathname;
  const expires: string | null = searchParams.get(URL_QUERY_PARAMS.EXPIRES);
  const signature: string | null = searchParams.get(URL_QUERY_PARAMS.SIGNATURE);

  const verifyEmailRequestURL = useMemo(() => {
    if (!pathname || !expires || !signature) return null;

    return `${pathname.replace(BASE_PAGE_PATH, '')}?${URL_QUERY_PARAMS.EXPIRES}=${expires}&${
      URL_QUERY_PARAMS.SIGNATURE
    }=${signature}`;
  }, [pathname, expires, signature]);

  const vefifyEmail = async (url: string) => {
    setError(null);
    setIsVerified(false);
    setIsLoading(true);

    try {
      const response = await verifyEmail(url);

      if (response && response.status === 200) {
        setIsVerified(true);
      }
    } catch (error: unknown) {
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmailVerification = async () => {
    setIsLoading(true);
    setIsVerified(false);
    setError(null);

    try {
      const response = await resendEmailVerification();

      if (response && response.status === 200) {
        setIsVerified(true);
      }
    } catch (error: unknown | Error) {
      setError(error as Error);
      setIsVerified(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  useEffect(() => {
    if (!verifyEmailRequestURL) return;

    vefifyEmail(verifyEmailRequestURL);
  }, [verifyEmailRequestURL]);

  useEffect(() => {
    if (!isVerified) return;
    const interval = setInterval(() => setRedirectCounter((counter) => counter - 1), 1000);
    return () => clearInterval(interval);
  }, [isVerified]);

  useEffect(() => {
    if (redirectCounter <= 0) {
      queryClient.invalidateQueries([QUERY_KEYS.ME]);
    }
  }, [redirectCounter]);

  if (isLoading) {
    return (
      <PageContainer>
        <Loader size='lg' theme='dark' />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div tw='m-auto w-full max-w-[470px]'>
        <Card>
          {error ? <ErrorIcon tw='m-auto' /> : <SuccessIcon tw='m-auto' />}
          <Title>
            {error
              ? t('auth.verify_email_page.title.error')
              : t('auth.verify_email_page.title.success')}
          </Title>
          <Spacer size='xs' direction='vertical' />
          <Subtitle>
            {error
              ? t('auth.verify_email_page.subtitle.error', {
                  error: error?.message || 'Something went wrong',
                })
              : t('auth.verify_email_page.subtitle.success')}
          </Subtitle>
          <Spacer size='xs' direction='vertical' />

          {error ? (
            <CardButton onClick={handleResendEmailVerification}>
              {t('auth.verify_email_page.action.error')}
            </CardButton>
          ) : (
            <CardFooter>
              {t('auth.verify_email_page.action.success', { count: redirectCounter })}
            </CardFooter>
          )}
        </Card>
        {error && <LogoutCTA onClick={handleLogout}>Logout</LogoutCTA>}
      </div>
    </PageContainer>
  );
};
